import { DropDownFieldDefinition, Option } from "@insite/client-framework/Types/FieldDefinition";
import get from "@insite/mobius/utilities/get";
import { StyledTheme } from "@store/Framework/Types/StyledTheme";

export type DataOptionMapperToValue<T> = {
    [K in keyof T]: T[K] extends DataOption<any> ? (T[K] extends DataOption<infer X> ? X : string) : unknown;
};

export interface DataOptions<T extends DataOption<any> = DataOption> {
    [key: string]: T;
}

export interface DataOption<T = string> {
    displayName?: string;
    id: string;
    isDefault?: boolean;
    value?: T;
    hidden?: boolean;
}

export const getDataOption = <T extends DataOption<any>>(
    options: DataOptions<T>,
    id: string | undefined,
): T | undefined => {
    return id ? Object.values(options).find(x => x.id === id) || options[id] : undefined;
};

export const getDataOptionValue = <T extends DataOption<any>, I extends T extends DataOption<infer U> ? U : any>(
    options: DataOptions<T>,
    id: string | undefined,
): I | undefined => {
    if (id) {
        for (const [key, option] of Object.entries(options)) {
            if (option.id === id) {
                return option.value ?? key;
            }
        }

        if (options[id]) {
            return options[id].value ?? id;
        }
    }

    return undefined;
};

export const getDataOptionThemeValue = <T>(options: DataOptions<any>, data: any, theme: StyledTheme): T | undefined => {
    if (typeof data === "string") {
        const value = getDataOptionValue(options, data);
        return typeof value === "string" ? get(theme, value) : value;
    }

    return data;
};

export const getFieldDefinitionDataOptions = <T extends DataOption<any>>(options: DataOptions<T>) => {
    return Object.values(options).reduce((result, { displayName, id, hidden }) => {
        if (!hidden) {
            return [...result, { displayName, value: id }];
        }

        return result;
    }, [] as Option<string>[]);
};

export const getFieldDefinitionDataOptionsDefaultValue = <T extends DataOption<any>>(options: DataOptions<T>) => {
    for (const { id, isDefault } of Object.values(options)) {
        if (isDefault) {
            return id;
        }
    }

    return "";
};

export const getDataOptionsFieldDefinition = <T extends DataOption<any>>(
    options: DataOptions<T>,
): Pick<DropDownFieldDefinition<string>, "editorTemplate" | "fieldType" | "options" | "defaultValue"> => {
    return {
        fieldType: "General",
        editorTemplate: "DropDownField",
        options: getFieldDefinitionDataOptions(options),
        defaultValue: getFieldDefinitionDataOptionsDefaultValue(options),
    };
};
